import React, { useState, useEffect } from 'react';
import {
  Users, Building2, Shield, User, Eye, EyeOff, Loader, Tag, Map, ExternalLink
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { useAlert } from '@/components/AlertProvider';
import api from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import gammeConfigService, { PaymentTerm } from '@/services/gammeConfigService';
import { EmailActionModal } from './EmailActionModal';

interface ClientCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (newClient: any) => void;
  editingClient?: any;
}

const ClientCreateModal: React.FC<ClientCreateModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  editingClient
}) => {
  const { user } = useAuthStore();
  const { showError, showSuccess } = useAlert();
  const [formStep, setFormStep] = useState<1 | 2 | 3 | 4 | 5>(1);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showMapPreview, setShowMapPreview] = useState(false);
  const [apporteurs, setApporteurs] = useState<any[]>([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [centralBuyers, setCentralBuyers] = useState<any[]>([]);
  const [loadingCentrals, setLoadingCentrals] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [paymentTermsList, setPaymentTermsList] = useState<PaymentTerm[]>([]);

  const brandOptions: string[] = ['NOVA FLORA', 'DOUCE FRANCE'];
  const defaultBrand: string = brandOptions[0];

  const [form, setForm] = useState<{
    company_name: string;
    marque: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    postal_code: string;
    country: string;
    maps_link: string;
    siret: string;
    website: string;
    pappers_url: string;
    infogreffe_url: string;
    pharmanity_url: string;
    password: string;
    is_active: boolean;
    status: string;
    client_type: string;
    parent_buyer_id: number | null;
    admin_id: number | null;
    payment_mode: string;
    payment_terms: string;
    invoice_email: string;
    central_purchasing_id: number | null;
    use_central_purchasing_bpu: boolean;
  }>({
    company_name: '',
    marque: defaultBrand,
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'France',
    maps_link: '',
    siret: '',
    website: '',
    pappers_url: '',
    infogreffe_url: '',
    pharmanity_url: '',
    password: '',
    is_active: true,
    status: 'prospect',
    client_type: 'pharmacie' as any,
    parent_buyer_id: null as number | null,
    admin_id: null as number | null,
    payment_mode: 'Virement',
    payment_terms: '',
    invoice_email: '',
    central_purchasing_id: null as number | null,
    use_central_purchasing_bpu: true,
  });

  const buildAddressComplete = (address: string, postalCode: string, city: string) => {
    return [address, postalCode, city]
      .map((v) => (typeof v === 'string' ? v.trim() : ''))
      .filter(Boolean)
      .join(' ');
  };

  const buildMapsLink = (addressComplete: string) => {
    const trimmed = addressComplete.trim();
    return trimmed ? `https://www.google.com/maps/search/${encodeURIComponent(trimmed)}` : '';
  };

  const buildMapsEmbedSrc = (addressComplete: string) => {
    const trimmed = addressComplete.trim();
    return trimmed
      ? `https://www.google.com/maps?q=${encodeURIComponent(trimmed)}&output=embed`
      : '';
  };

  useEffect(() => {
    if (editingClient) {
      setForm({
        company_name: editingClient.company_name || '',
        marque: editingClient.marque || defaultBrand,
        first_name: editingClient.first_name || '',
        last_name: editingClient.last_name || '',
        email: editingClient.email || '',
        phone: editingClient.phone || '',
        address: editingClient.address || '',
        city: editingClient.city || '',
        postal_code: editingClient.postal_code || '',
        country: editingClient.country || 'France',
        maps_link: editingClient.maps_link || '',
        siret: editingClient.siret || '',
        website: editingClient.website || '',
        pappers_url: editingClient.pappers_url || '',
        infogreffe_url: editingClient.infogreffe_url || '',
        pharmanity_url: editingClient.pharmanity_url || '',
        password: '',
        is_active: editingClient.is_active ?? true,
        status: editingClient.status || 'prospect',
        client_type: editingClient.client_type || 'pharmacie',
        parent_buyer_id: editingClient.parent_buyer_id || null,
        admin_id: editingClient.admin_id || null,
        payment_mode: editingClient.payment_mode || 'Virement',
        payment_terms: editingClient.payment_terms || (paymentTermsList.find(t => t.is_default)?.value ?? '30 jours FDM'),
        invoice_email: editingClient.invoice_email || '',
        central_purchasing_id: editingClient.central_purchasing_id || null,
        use_central_purchasing_bpu: editingClient.use_central_purchasing_bpu ?? true,
      });
    } else {
      setForm({
        company_name: '',
        marque: defaultBrand,
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        postal_code: '',
        country: 'France',
        maps_link: '',
        siret: '',
        website: '',
        pappers_url: '',
        infogreffe_url: '',
        pharmanity_url: '',
        password: '',
        is_active: true,
        status: 'prospect',
        client_type: 'pharmacie',
        parent_buyer_id: null,
        admin_id: user?.role === 'apporteur' ? user.id : null,
        payment_mode: 'Virement',
        payment_terms: paymentTermsList.find(t => t.is_default)?.value ?? '30 jours FDM',
        invoice_email: '',
        central_purchasing_id: null,
        use_central_purchasing_bpu: true,
      });
    }
    setShowMapPreview(false);
    setFormStep(1);
  }, [editingClient, isOpen, user, paymentTermsList]);

  useEffect(() => {
    gammeConfigService.listPaymentTerms().then(setPaymentTermsList).catch(console.error);
  }, []);

  const loadApporteurs = async () => {
    try {
      setAdminsLoading(true);
      const res = await api.get('/users/admins');
      const raw = res.data;
      const list = Array.isArray(raw) ? raw : (raw?.items || raw?.results || []);
      setApporteurs(list);
    } catch (e) {
      console.error('Load apporteurs error:', e);
    } finally {
      setAdminsLoading(false);
    }
  };

  const loadCentralBuyers = async () => {
    try {
      setLoadingCentrals(true);
      const res = await api.get('/clients/centrales');
      setCentralBuyers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Load central buyers error:', e);
    } finally {
      setLoadingCentrals(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadApporteurs();
      loadCentralBuyers(); // Charger les centrales immédiatement, pas seulement à l'étape 4
    }
  }, [isOpen]);

  const handleSaveClick = () => {
    if (!form.company_name.trim()) return showError("Le nom de l'entreprise est obligatoire");
    if (!form.email.trim()) return showError("L'email est obligatoire");
    if (form.client_type === 'member' && !form.parent_buyer_id) return showError("Un client membre doit être rattaché à une centrale");

    // Show email action modal
    setShowEmailModal(true);
  };

  const handleEmailActionConfirm = async (action: 'welcome' | 'reset' | 'none') => {
    await handleSave(action);
  };

  const handleSave = async (emailAction: 'welcome' | 'reset' | 'none') => {
    setSaving(true);
    try {
      let response;
      const { parent_buyer_id, ...rest } = form;
      const addressComplete = buildAddressComplete(rest.address, rest.postal_code, rest.city);
      const isAdminUser = user?.role === 'super_admin' || user?.role === 'admin';
      const { admin_id, ...restWithoutAdmin } = rest as any;
      const payload: any = {
        ...(isAdminUser ? rest : restWithoutAdmin),
        maps_link: buildMapsLink(addressComplete),
        parent_client_id: parent_buyer_id,
        password: form.password || '123456789',
        email_action: emailAction
      };

      if (!isAdminUser) {
        delete payload.admin_id;
        delete payload.admin;
        delete payload.administrateur;
      }

      if (editingClient && !form.password) delete payload.password;

      if (editingClient) {
        response = await api.patch(`/clients/${editingClient.id}`, payload);
        showSuccess('Client mis à jour');
      } else {
        response = await api.post('/clients', payload);
        showSuccess('Client créé');
      }
      onSuccess(response.data);
      onClose();
    } catch (e: any) {
      const errorMessage = typeof e.response?.data?.detail === 'string'
        ? e.response.data.detail
        : e.response?.data?.message || e.message || "Erreur lors de l'enregistrement";
      showError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {
      if (!showEmailModal) onClose();
    }}>
      <DialogContent
        className="max-w-2xl w-full max-h-[90vh] overflow-y-auto p-0 border-none bg-transparent [&>*]:pointer-events-auto"
        onPointerDownOutside={(e) => {
          if (showEmailModal) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (showEmailModal) e.preventDefault();
        }}
      >
        <VisuallyHidden>
          <DialogTitle>{editingClient ? 'Modifier' : 'Nouveau'} Client</DialogTitle>
        </VisuallyHidden>
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col pointer-events-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 sticky top-0 z-10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                <Users className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">{editingClient ? 'Modifier' : 'Nouveau'} Client</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {editingClient ? 'Modifier les informations' : `Étape ${formStep} sur ${user?.role === 'super_admin' ? '5' : (user?.role === 'admin' ? '4' : '3')}`}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">✕</button>
          </div>

          {/* Stepper */}
          <div className="px-6 pt-4">
            <div className="flex items-center justify-between">
              {[
                { step: 1, label: 'Entreprise', icon: Building2 },
                { step: 2, label: 'Contact', icon: User },
                ...(user?.role === 'admin' || user?.role === 'super_admin' ? [{ step: 3, label: 'Apporteur', icon: Shield }] : []),
                ...(user?.role === 'super_admin' ? [{ step: 4, label: 'Admin', icon: Shield }] : []),
                { step: user?.role === 'super_admin' ? 5 : (user?.role === 'admin' ? 4 : 3), label: 'Marque', icon: Tag },
              ].map((s, idx, arr) => (
                <div key={s.step} className="flex items-center flex-1">
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all cursor-pointer ${formStep >= s.step ? 'bg-purple-600 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-500'}`}
                      onClick={() => (editingClient || formStep > s.step) && setFormStep(s.step as any)}
                    >
                      {formStep > s.step ? '✓' : <s.icon className="h-4 w-4" />}
                    </div>
                    <span className={`text-xs mt-1.5 font-medium ${formStep >= s.step ? 'text-purple-600' : 'text-slate-400'}`}>{s.label}</span>
                  </div>
                  {idx < arr.length - 1 && <div className={`h-1 flex-1 mx-2 rounded ${formStep > s.step ? 'bg-purple-600' : 'bg-slate-200 dark:bg-slate-700'}`} />}
                </div>
              ))}
            </div>
          </div>

          <div className="p-6 space-y-4">
            {formStep === 1 && (
              <div className="space-y-4 animate-in fade-in slide-in-from-right-4">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase flex items-center gap-2"><Building2 className="h-4 w-4" /> Informations Entreprise</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label htmlFor="client_company_name" className="block text-sm font-medium mb-1">Nom de l'entreprise *</label>
                    <input id="client_company_name" className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} />
                  </div>
                  <div>
                    <label htmlFor="client_type" className="block text-sm font-medium mb-1">Type Client *</label>
                    <select id="client_type" className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={form.client_type} onChange={e => setForm({ ...form, client_type: e.target.value as any })}>
                      <option value="pharmacie">Pharmacie</option>
                      <option value="parapharmacie">Parapharmacie</option>
                      {user?.role === 'super_admin' && <option value="centrale">Centrale d'Achat</option>}
                      <option value="magasin_bio">Magasin Bio</option>
                      <option value="therapeute">Thérapeute</option>
                      <option value="member">Client Membre</option>
                      <option value="independent">Indépendant</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="client_siret" className="block text-sm font-medium mb-1">SIRET</label>
                    <input id="client_siret" className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={form.siret} onChange={e => setForm({ ...form, siret: e.target.value })} placeholder="Ex: 123 456 789 00012" />
                  </div>
                  <div>
                    <label htmlFor="client_website" className="block text-sm font-medium mb-1">
                      <span className="flex items-center gap-1.5">
                        Site web
                        <ExternalLink className="h-3 w-3 text-slate-400" />
                      </span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        id="client_website"
                        type="url"
                        className="flex-1 px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                        value={form.website}
                        onChange={e => setForm({ ...form, website: e.target.value })}
                        placeholder="https://www.exemple.com"
                      />
                      {form.website && (
                        <a
                          href={form.website}
                          target="_blank"
                          rel="noreferrer"
                          className="px-3 py-2 bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors flex items-center gap-1"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                  </div>
                  <div>
                    <label htmlFor="client_pappers_url" className="block text-sm font-medium mb-1">
                      <span className="flex items-center gap-1.5">
                        Pappers.fr
                        <ExternalLink className="h-3 w-3 text-slate-400" />
                      </span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        id="client_pappers_url"
                        type="url"
                        className="flex-1 px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                        value={form.pappers_url}
                        onChange={e => setForm({ ...form, pappers_url: e.target.value })}
                        placeholder="https://www.pappers.fr/entreprise/..."
                      />
                      {form.pappers_url && (
                        <a
                          href={form.pappers_url}
                          target="_blank"
                          rel="noreferrer"
                          className="px-3 py-2 bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors flex items-center gap-1"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                  </div>
                  <div>
                    <label htmlFor="client_infogreffe_url" className="block text-sm font-medium mb-1">
                      <span className="flex items-center gap-1.5">
                        Infogreffe
                        <ExternalLink className="h-3 w-3 text-slate-400" />
                      </span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        id="client_infogreffe_url"
                        type="url"
                        className="flex-1 px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                        value={form.infogreffe_url}
                        onChange={e => setForm({ ...form, infogreffe_url: e.target.value })}
                        placeholder="https://www.infogreffe.fr/entreprise/..."
                      />
                      {form.infogreffe_url && (
                        <a
                          href={form.infogreffe_url}
                          target="_blank"
                          rel="noreferrer"
                          className="px-3 py-2 bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors flex items-center gap-1"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                  </div>
                  <div>
                    <label htmlFor="client_pharmanity_url" className="block text-sm font-medium mb-1">
                      <span className="flex items-center gap-1.5">
                        Pharmanity
                        <ExternalLink className="h-3 w-3 text-slate-400" />
                      </span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        id="client_pharmanity_url"
                        type="url"
                        className="flex-1 px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                        value={form.pharmanity_url}
                        onChange={e => setForm({ ...form, pharmanity_url: e.target.value })}
                        placeholder="https://www.pharmanity.fr/entreprise/..."
                      />
                      {form.pharmanity_url && (
                        <a
                          href={form.pharmanity_url}
                          target="_blank"
                          rel="noreferrer"
                          className="px-3 py-2 bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors flex items-center gap-1"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">Localisation</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="sm:col-span-2">
                          <label htmlFor="client_address" className="block text-sm font-medium mb-1">Rue</label>
                          <input
                            id="client_address"
                            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                            value={form.address}
                            onChange={(e) =>
                              setForm((prev) => {
                                const next = { ...prev, address: e.target.value };
                                const addressComplete = buildAddressComplete(next.address, next.postal_code, next.city);
                                return { ...next, maps_link: buildMapsLink(addressComplete) };
                              })
                            }
                            placeholder="Ex: 44 Avenue Gambetta"
                          />
                        </div>
                        <div>
                          <label htmlFor="client_postal_code" className="block text-sm font-medium mb-1">Code postal</label>
                          <input
                            id="client_postal_code"
                            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                            value={form.postal_code}
                            onChange={(e) =>
                              setForm((prev) => {
                                const next = { ...prev, postal_code: e.target.value };
                                const addressComplete = buildAddressComplete(next.address, next.postal_code, next.city);
                                return { ...next, maps_link: buildMapsLink(addressComplete) };
                              })
                            }
                            placeholder="Ex: 17100"
                          />
                        </div>
                        <div>
                          <label htmlFor="client_city" className="block text-sm font-medium mb-1">Ville</label>
                          <input
                            id="client_city"
                            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                            value={form.city}
                            onChange={(e) =>
                              setForm((prev) => {
                                const next = { ...prev, city: e.target.value };
                                const addressComplete = buildAddressComplete(next.address, next.postal_code, next.city);
                                return { ...next, maps_link: buildMapsLink(addressComplete) };
                              })
                            }
                            placeholder="Ex: Saintes"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <label htmlFor="client_address_complete" className="block text-sm font-medium mb-1">Adresse complète</label>
                          <input
                            id="client_address_complete"
                            className="w-full px-3 py-2 border rounded-lg bg-slate-50 dark:bg-slate-800/60 border-slate-300 dark:border-slate-600"
                            value={buildAddressComplete(form.address, form.postal_code, form.city)}
                            readOnly
                          />
                          {buildAddressComplete(form.address, form.postal_code, form.city) && (
                            <div className="mt-2 flex flex-wrap items-center gap-3">
                              <button
                                type="button"
                                onClick={() => setShowMapPreview((v) => !v)}
                                className={
                                  "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors " +
                                  (showMapPreview
                                    ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/40 dark:text-blue-300'
                                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700')
                                }
                              >
                                <Map className="h-3.5 w-3.5" />
                                Carte
                              </button>
                              {form.maps_link && (
                                <a
                                  href={form.maps_link}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-sm text-blue-600 hover:underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                >
                                  Ouvrir dans Google Maps
                                </a>
                              )}
                            </div>
                          )}
                          {showMapPreview && (
                            <div className="mt-3 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                              <iframe
                                title="Google Maps"
                                src={buildMapsEmbedSrc(buildAddressComplete(form.address, form.postal_code, form.city))}
                                className="w-full h-64"
                                referrerPolicy="no-referrer-when-downgrade"
                                allowFullScreen
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase flex items-center gap-2 pt-2"><Building2 className="h-4 w-4" /> Données de facturation</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label htmlFor="client_invoice_email" className="block text-sm font-medium mb-1">Email de facturation</label>
                    <input id="client_invoice_email" type="email" className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={form.invoice_email} onChange={e => setForm({ ...form, invoice_email: e.target.value })} placeholder="Email pour les factures (si différent de l'email principal)" />
                  </div>
                  <div>
                    <label htmlFor="client_payment_mode" className="block text-sm font-medium mb-1">Mode de règlement</label>
                    <select id="client_payment_mode" className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={form.payment_mode} onChange={e => setForm({ ...form, payment_mode: e.target.value })}>
                      <option value="Virement">Virement</option>
                      <option value="Prélèvement">Prélèvement</option>
                      {/* <option value="Carte bancaire">Carte bancaire</option>
                      <option value="Chèque">Chèque</option>
                      <option value="Espèces">Espèces</option> */}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="client_payment_terms" className="block text-sm font-medium mb-1">Terme de règlement</label>
                    <select id="client_payment_terms" className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={form.payment_terms} onChange={e => setForm({ ...form, payment_terms: e.target.value })}>
                      {paymentTermsList.length > 0 ? (
                        paymentTermsList.map(t => (
                          <option key={t.id} value={t.value}>{t.label}</option>
                        ))
                      ) : (
                        <>
                          <option value="">Comptant</option>
                          <option value="30 jours Date Facture">30 jours Date Facture</option>
                          <option value="30 jours FDM">30 jours FDM</option>
                          <option value="45 jours FDM">45 jours FDM</option>
                          <option value="60 jours FDM">60 jours FDM</option>
                        </>
                      )}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {formStep === 2 && (
              <div className="space-y-4 animate-in fade-in slide-in-from-right-4">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase flex items-center gap-2"><User className="h-4 w-4" /> Contact Utilisateur</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="client_first_name" className="block text-sm font-medium mb-1">Prénom</label>
                    <input id="client_first_name" className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} placeholder="Ex : Marie" />
                  </div>
                  <div>
                    <label htmlFor="client_last_name" className="block text-sm font-medium mb-1">Nom</label>
                    <input id="client_last_name" className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} placeholder="Ex : Dupont" />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="client_email" className="block text-sm font-medium mb-1">Email *</label>
                    <input id="client_email" type="email" className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="client_password" className="block text-sm font-medium mb-1">Mot de passe</label>
                    <div className="relative">
                      <input id="client_password" type={showPassword ? 'text' : 'password'} className="w-full px-3 py-2 pr-10 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder={editingClient ? "Laisser vide pour ne pas changer" : "Mot de passe"} />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">{showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="client_phone" className="block text-sm font-medium mb-1">Téléphone</label>
                    <PhoneInput id="client_phone" value={form.phone} onChange={v => setForm({ ...form, phone: v })} className="w-full" />
                  </div>
                </div>
              </div>
            )}

            {formStep === 3 && (user?.role === 'admin' || user?.role === 'super_admin') && (
              <div className="space-y-4 animate-in fade-in slide-in-from-right-4">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase flex items-center gap-2"><Shield className="h-4 w-4" /> Assignation Apporteur</h3>
                <div className="space-y-4">
                  <label htmlFor="client_admin_id" className="block text-sm font-medium mb-1">Choisir l'apporteur d'affaires *</label>
                  <select id="client_admin_id" className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={form.admin_id || ''} onChange={e => setForm({ ...form, admin_id: e.target.value ? parseInt(e.target.value) : null })} disabled={adminsLoading}>
                    <option value="">Sélectionner un apporteur...</option>
                    {apporteurs.filter(a => a.role === 'apporteur').map(a => <option key={a.id} value={a.id}>{a.first_name} {a.last_name} ({a.email})</option>)}
                  </select>
                  {!form.admin_id && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-xs text-amber-800">⚠️ Aucun apporteur sélectionné. Le client sera assigné par défaut.</div>}

                  {user?.role === 'admin' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                      <div>
                        <label htmlFor="client_status" className="block text-sm font-medium mb-1">Statut</label>
                        <select
                          id="client_status"
                          className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                          value={form.status}
                          onChange={e => setForm({ ...form, status: e.target.value })}
                        >
                          <option value="prospect">Prospect</option>
                          <option value="actif">Actif</option>
                          <option value="inactif">Inactif</option>
                          <option value="bloque">Bloqué</option>
                          <option value="ferme">Fermé</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="client_is_active" className="block text-sm font-medium mb-1">Accès Compte</label>
                        <select
                          id="client_is_active"
                          className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                          value={form.is_active ? 'active' : 'inactive'}
                          onChange={e => setForm({ ...form, is_active: e.target.value === 'active' })}
                        >
                          <option value="active">Activé</option>
                          <option value="inactive">Désactivé</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {formStep === 4 && user?.role === 'super_admin' && (
              <div className="space-y-4 animate-in fade-in slide-in-from-right-4">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase flex items-center gap-2"><Shield className="h-4 w-4" /> Options Administration</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="client_status" className="block text-sm font-medium mb-1">Statut</label>
                    <select
                      id="client_status"
                      className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                      value={form.status}
                      onChange={e => setForm({ ...form, status: e.target.value })}
                    >
                      <option value="prospect">Prospect</option>
                      <option value="actif">Actif</option>
                      <option value="inactif">Inactif</option>
                      <option value="bloque">Bloqué</option>
                      <option value="ferme">Fermé</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="client_is_active" className="block text-sm font-medium mb-1">Accès Compte</label>
                    <select id="client_is_active" className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={form.is_active ? 'active' : 'inactive'} onChange={e => setForm({ ...form, is_active: e.target.value === 'active' })}>
                      <option value="active">Activé</option>
                      <option value="inactive">Désactivé</option>
                    </select>
                  </div>
                  {form.client_type === 'member' && (
                    <div className="sm:col-span-2">
                      <label htmlFor="client_parent_buyer_id" className="block text-sm font-medium mb-1">Centrale d'achat parente *</label>
                      <select id="client_parent_buyer_id" className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={form.parent_buyer_id || ''} onChange={e => setForm({ ...form, parent_buyer_id: e.target.value ? parseInt(e.target.value) : null })} onFocus={() => !centralBuyers.length && loadCentralBuyers()} disabled={loadingCentrals}>
                        {loadingCentrals ? <option>Chargement...</option> : <option value="">Sélectionner une centrale...</option>}
                        {centralBuyers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                      </select>
                      {form.parent_buyer_id && <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm text-blue-800">ℹ️ Le BPU sera hérité de la centrale sélectionnée.</div>}
                    </div>
                  )}

                  {form.client_type !== 'centrale' && (
                    <div className="sm:col-span-2 pt-4 mt-2 border-t border-slate-100 dark:border-slate-800">
                      <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3 flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-purple-500" />
                        Rattachement Centrale d'Achat
                      </h4>
                      <div className="space-y-4">
                        <div>
                          <label htmlFor="client_central_purchasing_id" className="block text-sm font-medium mb-1">Centrale de rattachement (Optionnel)</label>
                          <select
                            id="client_central_purchasing_id"
                            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                            value={form.central_purchasing_id || ''}
                            onChange={e => setForm({ ...form, central_purchasing_id: e.target.value ? parseInt(e.target.value) : null })}
                            onFocus={() => !centralBuyers.length && loadCentralBuyers()}
                            disabled={loadingCentrals}
                          >
                            <option value="">Aucune centrale rattachée</option>
                            {centralBuyers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                          </select>
                        </div>

                        {form.central_purchasing_id && (
                          <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-lg border border-slate-200 dark:border-slate-700 animate-in fade-in slide-in-from-top-2">
                            <label className="block text-sm font-medium mb-3 text-slate-900 dark:text-slate-100">Choix du BPU à appliquer :</label>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                              <label className="flex items-center gap-2 cursor-pointer group">
                                <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${form.use_central_purchasing_bpu ? 'border-purple-600 bg-purple-600' : 'border-slate-400 bg-white dark:bg-slate-700'}`}>
                                  {form.use_central_purchasing_bpu && <div className="w-2 h-2 rounded-full bg-white" />}
                                </div>
                                <input
                                  type="radio"
                                  name="bpu_mode"
                                  checked={form.use_central_purchasing_bpu === true}
                                  onChange={() => setForm({ ...form, use_central_purchasing_bpu: true })}
                                  className="sr-only"
                                />
                                <span className="text-sm font-medium group-hover:text-purple-600 transition-colors">Utiliser le BPU de la centrale</span>
                              </label>

                              <label className="flex items-center gap-2 cursor-pointer group">
                                <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${!form.use_central_purchasing_bpu ? 'border-purple-600 bg-purple-600' : 'border-slate-400 bg-white dark:bg-slate-700'}`}>
                                  {!form.use_central_purchasing_bpu && <div className="w-2 h-2 rounded-full bg-white" />}
                                </div>
                                <input
                                  type="radio"
                                  name="bpu_mode"
                                  checked={form.use_central_purchasing_bpu === false}
                                  onChange={() => setForm({ ...form, use_central_purchasing_bpu: false })}
                                  className="sr-only"
                                />
                                <span className="text-sm font-medium group-hover:text-purple-600 transition-colors">Utiliser le BPU propre du client</span>
                              </label>
                            </div>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 pl-6 border-l-2 border-slate-300 dark:border-slate-600 italic">
                              {form.use_central_purchasing_bpu
                                ? "Les modifications du BPU de la centrale seront automatiquement répercutées sur ce client."
                                : "Le client utilise exclusivement son propre BPU. Les changements de la centrale n'auront aucun impact."}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {((user?.role === 'super_admin' && formStep === 5) || (user?.role === 'admin' && formStep === 4) || (user?.role === 'apporteur' && formStep === 3)) && (
              <div className="space-y-4 animate-in fade-in slide-in-from-right-4">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase flex items-center gap-2"><Tag className="h-4 w-4" /> Marque</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label htmlFor="client_marque" className="block text-sm font-medium mb-1">Marque *</label>
                    <select
                      id="client_marque"
                      className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 focus:ring-2 focus:ring-purple-500 outline-none"
                      value={form.marque}
                      onChange={e => setForm({ ...form, marque: e.target.value })}
                      required
                    >
                      <option value="">Sélectionner une marque...</option>
                      {brandOptions.map((b) => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                    {!form.marque && (
                      <p className="text-xs text-amber-600 mt-1">⚠️ La sélection d'une marque est obligatoire pour générer le tarif (BPU).</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between gap-3 pt-6 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 sticky bottom-0 z-10 p-6">
              <button onClick={() => formStep === 1 ? onClose() : setFormStep(prev => (prev - 1) as any)} className="px-4 py-2 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">{formStep === 1 ? 'Annuler' : 'Précédent'}</button>
              <div className="flex gap-3">
                {formStep < (user?.role === 'super_admin' ? 5 : (user?.role === 'admin' ? 4 : 3)) ? (
                  <button onClick={() => setFormStep(prev => (prev + 1) as any)} disabled={formStep === 1 && !form.company_name.trim()} className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium">Suivant</button>
                ) : (
                  <button
                    onClick={handleSaveClick}
                    disabled={saving || !form.company_name.trim() || !form.email.trim() || !form.marque}
                    className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 font-medium flex items-center gap-2"
                  >
                    {saving && <Loader className="h-4 w-4 animate-spin" />}
                    {editingClient ? 'Enregistrer' : 'Créer le client'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>

      {/* Email Action Modal */}
      <EmailActionModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onConfirm={handleEmailActionConfirm}
        isNewClient={!editingClient}
        clientName={form.company_name || `${form.first_name} ${form.last_name}`.trim() || form.email}
      />
    </Dialog>
  );
};

export default ClientCreateModal;
